import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { get_po_serial_number, getShopsByCom, getWarehouses, transferProducts } from '../../../Utility/Api';
import { toast } from 'react-toastify';
import { WarehouseContext } from '../../../Utility/UserContext';

const ProductTransfer = () => {
    const location = useLocation();
    const { state } = location;
    const { showLoader, hideLoader, shopItem, subCompany, warehouse, UserItem } = useContext(WarehouseContext);

    // State management
    const [productSerialData, setProductSerialData] = useState([]);
    const [shopData, setShopData] = useState([]);
    const [warehouseData, setWarehouseData] = useState([]);
    const [selectedSerials, setSelectedSerials] = useState([]);
    const [selectedShop, setSelectedShop] = useState('');
    const [selectedWarehouse, setSelectedWarehouse] = useState('');
    const [step, setStep] = useState(1); // 1: Select serials, 2: Select destination, 3: Confirm
    const [filteredSerials, setFilteredSerials] = useState([]);

    // Fetch data on component mount
    useEffect(() => {
        if (state?.product_id) {
            fetchserialNo(state?.product_id);
            fetchShops();
        }
    }, [state, warehouse]);

    // API calls
    const fetchserialNo = async (PO_ID) => {
        try {
            showLoader();
            const response = await get_po_serial_number(PO_ID, shopItem?.shop_id);

            if (response.data.data) {
                const data = response.data.data;
                setProductSerialData(data);
                const fildata = data.filter(item => item.ware_id == warehouse?.id + "")
                setFilteredSerials(fildata);
            } else {
                toast.error('Serial numbers not found.');
            }
        } catch (error) {
            console.error('Error fetching serial numbers:', error);
            toast.error('Error fetching serial numbers');
        } finally {
            hideLoader();
        }
    };

    const fetchShops = async () => {
        try {
            showLoader();
            const response = await getShopsByCom(subCompany?.id);
            if (response.data && response.data.data) {
                const data = response.data.data.map((item) => ({
                    ...item,
                    shop_id: item.id,
                    shop_title: item.title,
                }));
                setShopData(data);
            }
        } catch (error) {
            console.error('Error fetching shops:', error);
            toast.error('Error fetching shops');
        } finally {
            hideLoader();
        }
    };

    const fetchWarehouses = async (shopID) => {
        try {
            showLoader();
            const response = await getWarehouses("", "", shopID);
            if (response && response.data) {
                setWarehouseData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching warehouses:', error);
            toast.error('Error fetching warehouses');
        } finally {
            hideLoader();
        }
    };

    // Event handlers
    const handleSerialSelect = (serialId) => {
        if (selectedSerials.includes(serialId)) {
            setSelectedSerials(selectedSerials.filter(id => id !== serialId));
        } else {
            setSelectedSerials([...selectedSerials, serialId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedSerials.length === filteredSerials.length) {
            setSelectedSerials([]);
        } else {
            setSelectedSerials(filteredSerials.map(item => item.id));
        }
    };

    const handleNext = () => {
        if (step === 1 && selectedSerials.length === 0) {
            toast.error('Please select at least one serial number');
            return;
        }
        if (step === 2 && (!selectedShop || !selectedWarehouse)) {
            toast.error('Please select both shop and warehouse');
            return;
        }
        setStep(step + 1);
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    const handleConfirm = async () => {
        try {
            showLoader();

            // Prepare the transfer payload
            const transferPayload = {
                product_id: state?.product_id,
                product_name: state?.product_name,
                from_shop_id: shopItem?.shop_id,
                from_ware_id: warehouse?.id, // You might need to adjust this based on your data structure
                to_shop_id: parseInt(selectedShop),
                to_ware_id: parseInt(selectedWarehouse),
                serial_numbers: selectedSerials.map(serialId => {
                    const product = productSerialData.find(p => p.id === serialId);
                    return product.serial_number;
                }),
                amount: selectedSerials.reduce((sum, serialId) => {
                    const product = productSerialData.find(p => p.id === serialId);
                    return sum + parseFloat(product?.purchase_price || 0);
                }, 0),
                created_by: UserItem?.user_id
            };

            console.log("Transfer Payload:", transferPayload);

            // Call the transfer API
            const response = await transferProducts(transferPayload);

            if (response.data && response.data.status) {
                toast.success(`Successfully transferred ${selectedSerials.length} items`);

                // Reset form
                setSelectedSerials([]);
                setSelectedShop('');
                setSelectedWarehouse('');
                setStep(1);

                // Refresh the serial numbers list
                if (state?.product_id) {
                    fetchserialNo(state?.product_id);
                }
            } else {
                toast.error(response.data?.message || 'Transfer failed');
            }
        } catch (error) {
            console.error('Error during transfer:', error);
            toast.error(error.response?.data?.message || 'Error during transfer');
        } finally {
            hideLoader();
        }
    };

    // Inline styles
    const styles = {
        pageWrapper: {
            padding: '20px',
            backgroundColor: '#f5f5f5',
            minHeight: '100vh'
        },
        card: {
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            overflow: 'hidden'
        },
        cardBody: {
            padding: '25px'
        },
        productTransfer: {
            maxWidth: '800px',
            margin: '0 auto',
            fontFamily: 'Arial, sans-serif'
        },
        header: {
            textAlign: 'center',
            marginBottom: '30px',
            color: '#333'
        },
        progressIndicator: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '30px',
            position: 'relative'
        },
        progressLine: {
            position: 'absolute',
            top: '15px',
            left: '0',
            right: '0',
            height: '2px',
            backgroundColor: '#ddd',
            zIndex: '1'
        },
        progressStep: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: '2'
        },
        stepNumber: {
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: '#ddd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '5px',
            color: 'white',
            fontWeight: 'bold'
        },
        activeStep: {
            backgroundColor: '#4CAF50'
        },
        stepLabel: {
            margin: '0',
            fontSize: '12px',
            color: '#666'
        },
        stepContainer: {
            backgroundColor: '#f9f9f9',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
        },
        stepTitle: {
            marginBottom: '20px',
            color: '#333'
        },
        selectAll: {
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
        },
        checkbox: {
            marginRight: '10px'
        },
        serialList: {
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #ddd',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: 'white'
        },
        serialItem: {
            padding: '8px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center'
        },
        formGroup: {
            marginBottom: '15px'
        },
        label: {
            display: 'block',
            marginBottom: '5px',
            fontWeight: 'bold',
            color: '#555'
        },
        select: {
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '16px'
        },
        buttonGroup: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px'
        },
        button: {
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
        },
        primaryButton: {
            backgroundColor: '#4CAF50',
            color: 'white'
        },
        secondaryButton: {
            backgroundColor: '#f1f1f1',
            color: '#333'
        },
        disabledButton: {
            backgroundColor: '#ddd',
            cursor: 'not-allowed',
            color: '#999'
        },
        confirmationDetails: {
            marginBottom: '20px'
        },
        detailItem: {
            marginBottom: '10px'
        },
        serialListUl: {
            listStyleType: 'none',
            padding: '0',
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #ddd',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: 'white'
        },
        serialListItem: {
            padding: '5px',
            borderBottom: '1px solid #eee'
        },
        sourceInfo: {
            backgroundColor: '#f0f8ff',
            padding: '15px',
            borderRadius: '6px',
            marginBottom: '20px',
            borderLeft: '4px solid #1890ff'
        }
    };

    // Render step 1: Serial number selection
    const renderSerialSelection = () => (
        <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>Select Product Serial Numbers</h2>

            <div style={styles.sourceInfo}>
                <p><strong>Source:</strong> {shopItem?.shop_title} (Current Shop)</p>
                <p><strong>Product:</strong> {state?.product_name}</p>
                <p><strong>Available:</strong> {filteredSerials.length} serial numbers</p>
            </div>

            <div style={styles.selectAll}>
                <input
                    type="checkbox"
                    id="select-all"
                    style={styles.checkbox}
                    checked={selectedSerials.length === filteredSerials.length && filteredSerials.length > 0}
                    onChange={handleSelectAll}
                    disabled={filteredSerials.length === 0}
                />
                <label htmlFor="select-all">Select All ({filteredSerials.length} items)</label>
            </div>

            {filteredSerials.length > 0 ? (
                <div style={styles.serialList}>
                    {filteredSerials
                        .map(product => (
                            <div key={product.id} style={styles.serialItem}>
                                <input
                                    type="checkbox"
                                    id={`serial-${product.id}`}
                                    style={styles.checkbox}
                                    checked={selectedSerials.includes(product.id)}
                                    onChange={() => handleSerialSelect(product.id)}
                                />
                                <label htmlFor={`serial-${product.id}`}>
                                    {product.serial_number}
                                </label>
                            </div>
                        ))}
                </div>
            ) : (
                <p>No serial numbers available for transfer.</p>
            )}

            <div style={styles.buttonGroup}>
                <button
                    style={{ ...styles.button, ...styles.primaryButton, ...(selectedSerials.length === 0 && styles.disabledButton) }}
                    onClick={handleNext}
                    disabled={selectedSerials.length === 0}
                >
                    Next ({selectedSerials.length} selected)
                </button>
            </div>
        </div>
    );

    // Render step 2: Destination selection
    const renderDestinationSelection = () => (
        <div style={styles.stepContainer}>
            <h2 style={styles.stepTitle}>Select Destination</h2>

            <div style={styles.sourceInfo}>
                <p><strong>Transferring:</strong> {selectedSerials.length} serial numbers of {state?.product_name}</p>
            </div>

            <div style={styles.formGroup}>
                <label htmlFor="shop-select" style={styles.label}>Destination Shop:</label>
                <select
                    id="shop-select"
                    style={styles.select}
                    value={selectedShop}
                    onChange={(e) => {
                        setSelectedShop(e.target.value);
                        setSelectedWarehouse(''); // Reset warehouse when shop changes
                        fetchWarehouses(e.target.value);
                    }}
                >
                    <option value="">Select a shop</option>
                    {shopData
                        // .filter(shop => shop.shop_id !== shopItem?.shop_id) // Exclude current shop
                        .map(shop => (
                            <option key={shop.shop_id} value={shop.shop_id}>
                                {shop.shop_title}
                            </option>
                        ))}
                </select>
            </div>

            <div style={styles.formGroup}>
                <label htmlFor="warehouse-select" style={styles.label}>Destination Warehouse:</label>
                <select
                    id="warehouse-select"
                    style={styles.select}
                    value={selectedWarehouse}
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                    disabled={!selectedShop}
                >
                    <option value="">Select a warehouse</option>
                    {warehouseData.map(warehouse => (
                        <option key={warehouse.id} value={warehouse.id}>
                            {warehouse.title} ({warehouse.address})
                        </option>
                    ))}
                </select>
            </div>

            <div style={styles.buttonGroup}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleBack}>
                    Back
                </button>
                <button
                    style={{ ...styles.button, ...styles.primaryButton, ...((!selectedShop || !selectedWarehouse) && styles.disabledButton) }}
                    onClick={handleNext}
                    disabled={!selectedShop || !selectedWarehouse}
                >
                    Next
                </button>
            </div>
        </div>
    );

    // Render step 3: Confirmation
    const renderConfirmation = () => {
        const selectedShopData = shopData.find(s => s.shop_id === parseInt(selectedShop));
        const selectedWarehouseData = warehouseData.find(w => w.id === parseInt(selectedWarehouse));

        return (
            <div style={styles.stepContainer}>
                <h2 style={styles.stepTitle}>Confirm Transfer</h2>

                <div style={styles.confirmationDetails}>
                    <div style={styles.sourceInfo}>
                        <h3>Transfer Summary:</h3>
                        <p style={styles.detailItem}><strong>Product:</strong> {state?.product_name}</p>
                        <p style={styles.detailItem}><strong>Number of Items:</strong> {selectedSerials.length}</p>
                        <p style={styles.detailItem}><strong>From Shop:</strong> {shopItem?.shop_title}</p>
                        <p style={styles.detailItem}><strong>To Shop:</strong> {selectedShopData?.shop_title}</p>
                        <p style={styles.detailItem}><strong>To Warehouse:</strong> {selectedWarehouseData?.title}</p>
                    </div>

                    <h3>Selected Serial Numbers:</h3>
                    <ul style={styles.serialListUl}>
                        {selectedSerials.map(serialId => {
                            const product = productSerialData.find(p => p.id === serialId);
                            return <li key={serialId} style={styles.serialListItem}>{product.serial_number}</li>;
                        })}
                    </ul>
                </div>

                <div style={styles.buttonGroup}>
                    <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleBack}>
                        Back
                    </button>
                    <button style={{ ...styles.button, ...styles.primaryButton }} onClick={handleConfirm}>
                        Confirm Transfer
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.card}>
                <div style={styles.cardBody}>
                    <div style={styles.productTransfer}>
                        <h1 style={styles.header}>Product Transfer</h1>

                        <div style={styles.progressIndicator}>
                            <div style={styles.progressStep}>
                                <span style={{ ...styles.stepNumber, ...(step >= 1 && styles.activeStep) }}>1</span>
                                <p style={styles.stepLabel}>Select Serials</p>
                            </div>
                            <div style={styles.progressStep}>
                                <span style={{ ...styles.stepNumber, ...(step >= 2 && styles.activeStep) }}>2</span>
                                <p style={styles.stepLabel}>Select Destination</p>
                            </div>
                            <div style={styles.progressStep}>
                                <span style={{ ...styles.stepNumber, ...(step >= 3 && styles.activeStep) }}>3</span>
                                <p style={styles.stepLabel}>Confirm</p>
                            </div>
                            <div style={styles.progressLine}></div>
                        </div>

                        {step === 1 && renderSerialSelection()}
                        {step === 2 && renderDestinationSelection()}
                        {step === 3 && renderConfirmation()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductTransfer;